import cn from 'classnames';
import moment from 'moment';
import React from 'react';
import {
  TenancyOption,
  TenancyOptionsListItem,
} from '~components/datepicker/tenancy-option-helpers';
import Divider from '~components/divider';
import { ListGroup, ListItem } from '~components/list';
import Typography from '~components/typography';
import {
  TenancyEndDateTypeOption,
  TenancyLengthOptions,
  TenancyLengthTypeOption,
  TenancyLengthUnitType,
  TenancyOptionRateTypeOption,
  TenancyStartDateTypeOption,
  TenancyTypeOption,
} from '~graphql/__generated__/globalTypes';
import { useTranslation } from '~i18n';
import styles from './tenancy-option-group.module.scss';

interface ISelectTenancyTypes {
  tenancyOptionId: string;
  tenancyOptionStartDate?: Date;
  tenancyOptionEndDate?: Date;
  tenancyOptionRateType?: TenancyOptionRateTypeOption;
  academicYearId?: string;
  tenancyLengthOption?: TenancyLengthOptions;
  tenancyType: TenancyTypeOption;
  selectableStartDates?: string[];
}

interface ITenancyOptionGroupProps {
  tenancyOptionList: TenancyOptionsListItem[];
  onSelect: (params: ISelectTenancyTypes) => void;
  selectedValue: string;
  className?: string;
}

const TenancyOptionGroup: React.FC<ITenancyOptionGroupProps> = ({
  tenancyOptionList,
  onSelect,
  selectedValue,
  className,
}) => {
  const { t } = useTranslation(['student_portal']);

  const getTenancyLengthTypeLable = (tenancyLengthType: TenancyLengthTypeOption | null) => {
    if (tenancyLengthType === TenancyLengthTypeOption.LE) {
      return t('max');
    }

    if (tenancyLengthType === TenancyLengthTypeOption.GE) {
      return t('min');
    }

    return;
  };

  const getTenancyOptionUnit = (tenancyOption: TenancyOption) => {
    if (tenancyOption?.tenancyLengthUnit === TenancyLengthUnitType.MONTHS) {
      return Number(tenancyOption.tenancyLength) > 1 ? t('month_plural') : t('month');
    }
    if (tenancyOption?.tenancyLengthUnit === TenancyLengthUnitType.NIGHTS) {
      return Number(tenancyOption.tenancyLength) > 1 ? t('night_plural') : t('night');
    }
  };

  const getFlexiableLabel = (tenancy: TenancyOption) => {
    if (tenancy.tenancyType !== TenancyTypeOption.FLEXIBLE) return;
    const flexibleTenancyOptionKeyParams = {
      tenancyLengthTypeLable: getTenancyLengthTypeLable(tenancy.tenancyLengthType),
      startDate: moment(tenancy.startDate).format('ddd, ll'),
      endDate: moment(tenancy.endDate).format('ddd, ll'),
      tenancyLength: tenancy.tenancyLength,
      unit: getTenancyOptionUnit(tenancy),
    };
    if (tenancy.startDateType === TenancyStartDateTypeOption.EQ) {
      return t(
        'select_bed.tenancy_option.flexible.starting_on_start_date',
        flexibleTenancyOptionKeyParams
      );
    }

    if (tenancy.endDateType === TenancyEndDateTypeOption.EQ) {
      return t(
        'select_bed.tenancy_option.flexible.ending_on_end_date',
        flexibleTenancyOptionKeyParams
      );
    }

    return t(
      'select_bed.tenancy_option.flexible.stay_between_start_date_and_end_date',
      flexibleTenancyOptionKeyParams
    );
  };

  return (
    <ListGroup className={cn(styles.root, className)}>
      {tenancyOptionList.map(({ academicYear, tenancyOptions }) => {
        return (
          <div key={academicYear.id}>
            <div className={styles.academicYear}>
              <Typography variant="body3">{academicYear.name}</Typography>
            </div>
            {tenancyOptions?.map((tenancyOption, index) => {
              return (
                <div key={tenancyOption.id?.toString()}>
                  <ListItem
                    onClick={() => {
                      if (tenancyOption.tenancyType === TenancyTypeOption.FLEXIBLE) {
                        onSelect({
                          tenancyOptionId: tenancyOption.id,
                          tenancyOptionRateType: tenancyOption?.rateType,
                          academicYearId: academicYear.id,
                          tenancyLengthOption: tenancyOption.tenancyLengthOption,
                          tenancyType: tenancyOption.tenancyType,
                          selectableStartDates: tenancyOption.selectableStartDates,
                        });
                        return;
                      }

                      onSelect({
                        tenancyOptionId: tenancyOption.id,
                        tenancyOptionStartDate: moment(tenancyOption.startDate).toDate(),
                        tenancyOptionEndDate: moment(tenancyOption.endDate).toDate(),
                        tenancyOptionRateType: tenancyOption?.rateType,
                        academicYearId: academicYear.id,
                        tenancyLengthOption: tenancyOption.tenancyLengthOption,
                        tenancyType: tenancyOption.tenancyType,
                      });
                    }}
                    className={styles.listItem}
                    isSelected={selectedValue === tenancyOption.id}
                  >
                    <div className={styles.tenancyOption}>
                      <Typography className={styles.tenancyOptionName} variant="body3">
                        {tenancyOption.name}
                      </Typography>

                      {tenancyOption.tenancyType === TenancyTypeOption.FLEXIBLE && (
                        <Typography color="var(--color-purple_3)" variant="body3">
                          {getFlexiableLabel(tenancyOption)}
                        </Typography>
                      )}

                      {tenancyOption.tenancyType !== TenancyTypeOption.FLEXIBLE &&
                        tenancyOption?.tenancyLengthOption === TenancyLengthOptions.ROLLING && (
                          <Typography color="var(--color-purple_3)" variant="body3">
                            {`${t('from')} ${moment(tenancyOption.startDate).format('ddd, ll')}`}
                          </Typography>
                        )}

                      {tenancyOption.tenancyType !== TenancyTypeOption.FLEXIBLE &&
                        tenancyOption?.tenancyLengthOption !== TenancyLengthOptions.ROLLING && (
                          <Typography color="var(--color-purple_3)" variant="body3">
                            {`${moment(tenancyOption.startDate).format('ddd, ll')} - ${moment(
                              tenancyOption.endDate
                            ).format('ddd, ll')}`}
                          </Typography>
                        )}
                    </div>
                  </ListItem>
                  {index !== tenancyOptions?.length - 1 && <Divider />}
                </div>
              );
            })}
          </div>
        );
      })}
    </ListGroup>
  );
};
export default TenancyOptionGroup;
